import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { debounceTime, distinctUntilChanged, filter, map, merge } from "rxjs";
import { FormField } from "src/app/components/default/form/form-field";
import { JsonUtils } from "src/classes/JsonUtils";
import { ALLOWED_CHARACTERS } from "src/config/allowed-characters.config";
import { ROUTES_CONFIG } from "src/config/routes.config";
import { Message } from "src/interfaces/post-request/post-request";
import { ApplicationService } from "src/services/application.service";
import { HttpService } from "src/services/http.service";
import { SessionService } from "src/services/session.service";
import { PrefixComponent } from "../../prefix.component";
import { TemplateTextComponent } from "../../templates/template-text/template-text.component";

@Component({
  selector: "app-prefix-jha",
  imports: [TemplateTextComponent],
  templateUrl: "./prefix-jha.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./prefix-jha.component.less",
})
export class PrefixJhaComponent extends PrefixComponent<string> implements OnInit {
  @Input()
  public ffwdactionid: string;

  @Input()
  public listen: FormField[];

  @Input()
  public input: { param: string; field: FormField | undefined }[];

  @Input()
  public output: { param: string; field: FormField | undefined }[];

  private http = inject(HttpService);
  private session = inject(SessionService);
  private application = inject(ApplicationService);

  public constructor() {
    super();
    this.hide = true;
    this.characters = ALLOWED_CHARACTERS["jha"];
    this.listen = [];
    this.input = [];
    this.output = [];
    this.ffwdactionid = "";
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.addSubscription(
      merge(...this.listen.map((field) => field.value), ...this.listen.map((field) => field.touched$))
        .pipe(
          filter(() => {
            return (
              this.listen.every((field) => {
                if (field.errors.size && (field.condition.value || field.hidden)) {
                  return false;
                }
                return true;
              }) && this.input.every(({ field }) => field && field.errors.size === 0)
            );
          }),
          debounceTime(2000),
          map(() => {
            const payload: Record<string, unknown> = {};
            for (const { field, param } of this.input) {
              if (!field) continue;

              if (param.startsWith("arr") && field.extras && "listvalues" in field.extras) {
                payload[param] = field.extras["listvalues"];
              } else {
                payload[param] = field.value.value;
              }
            }
            return payload;
          }),
          distinctUntilChanged((prev, cur) => {
            return JSON.stringify(prev) === JSON.stringify(cur);
          }),
        )
        .subscribe((payload) => {
          this.executeAction(payload);
        }),
    );
  }

  private async executeAction(payload: Record<string, unknown>): Promise<void> {
    try {
      const endpoint = this.session.authenticated.value ? ROUTES_CONFIG.actionurl : ROUTES_CONFIG.unsecureactionurl;
      const { postActionAsJSON } = await this.http.send<{ postActionAsJSON: string }>(endpoint, {
        FFWDActionID: this.ffwdactionid,
        actionData: JSON.stringify(payload),
      });
      const response = JsonUtils.tryParse<{ result: string; messages?: Message[]; actionData: Record<string, unknown> }>(postActionAsJSON);

      if (response.messages?.length) {
        this.application.onMessage(response.messages);
      }

      for (const { param, field } of this.output) {
        if (param in response.actionData) {
          field?.value.next(response.actionData[param]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }
}
